import { useEffect, useRef, useState } from 'react'
import type { CourseUpdate, CourseWithoutBlocks, Decimal } from '@/types/course'
import { useUpdateCourseMutation } from '../../coursesApi'
import { useAppDispatch } from '@/store/hooks'
import { setSaveStatus } from '../../editorSlice'
import {
  clearable,
  clearableNumber,
  priceThresholdError,
  toNumber,
  PRICE_RUB_MIN,
  PRICE_USD_MIN,
} from '../../lib/coursePatch'
import { readSalesModel } from '../../lib/salesModel'
import { trialLessonsLimit } from '../../lib/trialLimit'

// Поля CourseUpdate, где «очищенное» значение — число 0, а не пустая строка.
const NUMERIC_FIELDS = new Set<keyof CourseUpdate>(['basePrice', 'trialLessonsCount'])

// Поля-списки CourseUpdate — бэк заменяет список целиком, «очищенное» значение — [].
const LIST_FIELDS = new Set<keyof CourseUpdate>(['mascots', 'categoryIds', 'styleReferences'])

// Цены уходят как есть: явный null стирает цену на бэке («В подписке»), а 0 значит
// «бесплатно для всех» — превращать null в 0 нельзя.
const NULLABLE_PRICE_FIELDS = new Set<keyof CourseUpdate>(['usdPrice', 'priceRub'])

// Для остальных полей null не обнуляет значение на бэке (см. docs/backend/gamified-course-update-fields.md),
// поэтому РЕАЛЬНО изменённые (уже прошедшие фильтр change-detection) поля переводим
// в «пустое» значение нужного типа перед отправкой. Object.fromEntries стирает
// пер-ключевые типы CourseUpdate обратно в Record<string, unknown>, поэтому cast
// на выходе неизбежен — сам по себе он ничего не проверяет и не скрывает логику.
const withClearedNulls = (changed: CourseUpdate): CourseUpdate =>
  Object.fromEntries(
    Object.entries(changed).map(([key, value]) => {
      if (value !== null) return [key, value]
      const field = key as keyof CourseUpdate
      if (NULLABLE_PRICE_FIELDS.has(field)) return [key, null]
      if (LIST_FIELDS.has(field)) return [key, []]
      return [key, NUMERIC_FIELDS.has(field) ? clearableNumber(value) : clearable(value)]
    }),
  ) as CourseUpdate

// null/undefined (cleared field) is always flushable — toNumber turns it into 0,
// which passes every threshold. Only a real, un-blurred invalid number is rejected.
// zeroAllowed приходит СНАРУЖИ, от вызывающего — по СОХРАНЁННОМУ курсу (readSalesModel(course)
// в flushPendingChanges), а не по local: обнулённое поле само двигает readSalesModel(local)
// к 'free', и рассуждение зациклилось бы, снова разрешив ноль всегда. `course` меняется
// только после сохранения, а смена режима на «Бесплатный» сохраняется сразу при клике
// (см. PricingSection.changeModel), поэтому к моменту flush course уже отражает free.
//
// Расхождение источников: blur внутри PricingSection считает zeroAllowed по
// resolveSalesModel(derived, override) — живому, с учётом только что выбранного автором
// режима; flush здесь — по readSalesModel(course) БЕЗ оверрайда, то есть по последнему
// сохранённому курсу. В окне между кликом по радио и рефетчем эти два источника могут
// разойтись, но безвредно ровно по двум причинам. Во-первых, сам переход режима
// (isAvailableBySubscription) никогда не идёт через flush — changeModel шлёт его своим
// save(patch) синхронно по клику, до flush дело не доходит. Во-вторых, цену save()
// всё равно шлёт только если она РЕАЛЬНО отличается от savedRef (см. save ниже): если
// readSalesModel(course) даёт zeroAllowed=true по ошибке (это возможно только когда
// курс уже 'free', то есть обе цены на сервере УЖЕ 0), local-нуль совпадает с savedRef
// и отбрасывается диффом — отправлять нечего. Если наоборот даёт zeroAllowed=false по
// ошибке (курс всё ещё 'subscription', для которого верно true) — незаблюренный ноль
// просто не флашится и старая цена остаётся на сервере, но это ни на что не влияет:
// readSalesModel проверяет isAvailableBySubscription раньше цены, и курс с подпиской
// продолжает считаться и показываться «по подписке» независимо от того, что лежит в
// priceRub/usdPrice. Автор при следующем открытии таба увидит верный zeroAllowed и
// сможет подтвердить ноль явным blur.
const isPriceFlushable = (
  value: Decimal | null | undefined,
  min: number,
  zeroAllowed: boolean,
): boolean => priceThresholdError(toNumber(value), min, zeroAllowed) === null

// Form state for the course settings dialog. Text/number fields live in
// `local` and are saved on blur; `save` filters out unchanged fields so blur
// events and dialog close never send no-op requests.
export const useCourseSettingsForm = (course: CourseWithoutBlocks, open: boolean) => {
  const dispatch = useAppDispatch()
  const [local, setLocal] = useState<CourseWithoutBlocks>(course)
  const [updateCourse] = useUpdateCourseMutation()

  // Last values persisted to the server; used to skip no-op saves.
  const savedRef = useRef<Record<string, unknown>>({ ...course })

  // Sync local form state only when the dialog (re)opens — not on every course
  // refetch, otherwise an in-flight save would clobber text being typed.
  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setLocal(course)
      savedRef.current = { ...course }
    }
    prevOpenRef.current = open
  }, [open, course])

  // Возвращает УСПЕХ сохранения: вызывающие, которые удаляют файл из хранилища
  // (MascotSlot/MascotsEditor/ImageUpload), обязаны дождаться прошедшего PUT — иначе при
  // 403/сети блоб уже удалён, а курс всё ещё на него ссылается. No-op (нечего сохранять)
  // — тоже успех: на сервере уже то, что нужно.
  const save = async (patch: CourseUpdate): Promise<boolean> => {
    const changed = Object.fromEntries(
      Object.entries(patch).filter(([key, value]) => savedRef.current[key] !== value),
    ) as CourseUpdate
    if (Object.keys(changed).length === 0) return true
    // Очищаем null → '' / 0 только сейчас, когда поле уже прошло фильтр «реально
    // изменилось» — иначе untouched null (совпадающий с сохранённым) слался бы как 0.
    const toSend = withClearedNulls(changed)
    const prevSaved = savedRef.current
    savedRef.current = { ...prevSaved, ...toSend }
    dispatch(setSaveStatus('saving'))
    try {
      await updateCourse({ id: course.id, patch: toSend }).unwrap()
      dispatch(setSaveStatus('saved'))
      return true
    } catch {
      savedRef.current = prevSaved
      dispatch(setSaveStatus('error'))
      return false
    }
  }

  // Blur may not fire when the dialog closes (Escape, close button) — flush
  // text/number fields still pending; `save` filters out unchanged ones. Values
  // stay raw (null included) so change-detection in `save` can see "unchanged".
  const flushPendingChanges = () => {
    const patch: CourseUpdate = {
      name: local.name,
      nameEn: local.nameEn,
      description: local.description,
      descriptionEn: local.descriptionEn,
      // Blur не срабатывает при закрытии по Escape/крестику — без этих двух
      // строк правка промпта баннера и стиля терялась бы молча.
      bannerPrompt: local.bannerPrompt,
      imageStyle: local.imageStyle,
    }
    // `basePrice` во flush больше нет: поле убрано из UI (п.11), править его из
    // студии нечем, и строка была бы мёртвой машинерией, читающейся как живая
    // валидация. Само поле контракта не тронуто — бэк по-прежнему его хранит.
    // Below-threshold price left un-blurred (Escape/close) must not reach the server —
    // the input already shows the error, but flush used to bypass it entirely and send
    // an invalid PUT the backend rejects wholesale. null/cleared is always valid (→ 0)
    // ТОЛЬКО когда курс сейчас бесплатный — в платном режиме 0 такая же ошибка порога,
    // как и любое значение ниже минимума провайдера (см. isPriceFlushable выше).
    const zeroAllowed = readSalesModel(course) === 'free'
    // Триал выше лимита не шлём: UI его не срезает, а бэк отказывает при росте (422).
    if (local.trialLessonsCount <= trialLessonsLimit(local.lessons.length))
      patch.trialLessonsCount = local.trialLessonsCount
    // У сохранённо-бесплатного курса локальный null — стёртая цена, а явный null на бэке
    // стирает её совсем; для бесплатного это 0. Нетронутый null (сохранён тоже null) не шлём.
    const freePrice = (field: 'usdPrice' | 'priceRub') =>
      local[field] == null && zeroAllowed && savedRef.current[field] != null ? 0 : local[field]
    if (isPriceFlushable(local.usdPrice, PRICE_USD_MIN, zeroAllowed))
      patch.usdPrice = freePrice('usdPrice')
    if (isPriceFlushable(local.priceRub, PRICE_RUB_MIN, zeroAllowed))
      patch.priceRub = freePrice('priceRub')
    void save(patch)
  }

  return { local, setLocal, save, flushPendingChanges }
}
